import { DatePicker, Input, Modal, Form, Spin } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useWatch } from 'antd/es/form/Form'
import { AmendmentFormData } from '../types/types'
import { FormattedInputNumber } from '../../../components/Misc/FormattedInputNumber'
import { useResetFormOnCloseModal } from '../../../components/Form/hooks'
import { statementRowSelector } from '../../../redux/entities/statementRows/selectors'
import { useAccounts } from '../../account/queries/useFetchAccounts'
import AccountSelect from '../../account/components/AccountSelect'
import { useAmendmentDetailQuery } from '../queries/useAmendmentDetailQuery'
import { EnhancedStatementRow } from '../../financialStatements/utils/dataEnhancement'
import { useCreateAmendment } from '../hooks/useCreateAmendment'
import { useUpdateAmendment } from '../hooks/useUpdateAmendment'
import { formatAsDate } from '../../../config/dayjsConfig'
import { Amendment } from '../../financialStatements'

interface AmendmentModalProps {
  amendmentId?: number
  rowData: EnhancedStatementRow
  visible: boolean
  onClose: () => void
}

const AmendmentModal = ({ visible, amendmentId, rowData, onClose }: AmendmentModalProps) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<AmendmentFormData>()
  const { data: accounts } = useAccounts()
  const balanceSheetRowId = useWatch('balanceSheetRowId', form)
  const statementRows = useSelector(statementRowSelector)

  // Fetch amendment detail data when editing
  const { data: amendmentDetail, isLoading: isLoadingDetail } = useAmendmentDetailQuery(amendmentId)

  // Mutations
  const { mutateAsync: createAmendment, isPending: isCreating } = useCreateAmendment()
  const { mutateAsync: updateAmendment, isPending: isUpdating } = useUpdateAmendment()

  const loading = isCreating || isUpdating

  useResetFormOnCloseModal({
    form,
    open: visible
  })

  const getInitialValues = (): AmendmentFormData => {
    // If editing (amendmentDetail from query), use its values
    if (amendmentDetail) {
      return {
        id: amendmentDetail.id,
        balanceSheetRowId: amendmentDetail.balanceSheetRowId,
        counterPartRowId: amendmentDetail.counterPartRowId,
        budgetingScenarioId: amendmentDetail.budgetingScenarioId,
        value: amendmentDetail.value,
        description: amendmentDetail.description,
        date: amendmentDetail.date ? dayjs(amendmentDetail.date, 'YYYY-MM-DD') : undefined,
        accountCode: amendmentDetail.accountCode,
        counterPartAccountCode: amendmentDetail.counterPartAccountCode
      }
    }

    // Otherwise, creating new amendment from rowData
    const counterPartRowId = statementRows?.find(r => r.id === rowData?.id)?.amendmentCounterPartRowIds?.[0]

    return {
      counterPartRowId,
      accountCode: rowData._sourceAccountCode,
      balanceSheetRowId: rowData._sourceStatementRowId,
      value: undefined,
      date: undefined,
      description: ''
    }
  }

  useEffect(() => {
    if (visible) {
      const values = getInitialValues()
      form.setFieldsValue(values)
    }
  }, [visible, amendmentDetail, rowData, statementRows])

  const counterPartSelectOptions = (id: number | undefined) => {
    if (!id) return <AccountSelect balanceSheetRowIds={[]} />
    const counterPartRowsIds = statementRows.find(row => row.id === id)?.amendmentCounterPartRowIds || []
    return <AccountSelect balanceSheetRowIds={counterPartRowsIds} />
  }

  const handleOk = async () => {
    try {
      const values = await form.validateFields()

      // Convert Dayjs date to string format for API
      const formattedDate = formatAsDate(values.date)
      if (!formattedDate) {
        throw new Error('Invalid date')
      }

      if (amendmentId) {
        // Update existing amendment - all required fields are validated
        const amendmentData: Amendment = {
          id: amendmentId,
          date: formattedDate,
          value: values.value!,
          description: values.description!,
          balanceSheetRowId: values.balanceSheetRowId!,
          counterPartRowId: values.counterPartRowId!,
          budgetingScenarioId: values.budgetingScenarioId,
          accountCode: values.accountCode,
          counterPartAccountCode: values.counterPartAccountCode
        }
        await updateAmendment(amendmentData)
      } else {
        // Create new amendment
        const amendmentData = {
          ...values,
          date: formattedDate
        }
        await createAmendment(amendmentData)
      }

      form.resetFields()
      onClose()
    } catch (error) {
      console.error('Validate or save failed:', error)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  // Show loading spinner while fetching detail data
  if (amendmentId && isLoadingDetail) {
    return (
      <Modal open={visible} footer={null} onCancel={handleCancel}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      title={t('financialStatementsPage:amendment')}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={t('global:save')}
      maskClosable={false}
      cancelText={t('global:cancel')}
      confirmLoading={loading}
      destroyOnHidden
      forceRender={false}
    >
      <Form
        name="AmendmentForm"
        className="loan-form"
        layout="vertical"
        form={form}
        onValuesChange={({ counterPartAccountCode }) => {
          // Accountcodea vaihdettaessa muutetaan counterPartRowId tilin arvoa vastaamaan
          if (counterPartAccountCode) {
            const { statementRowId } = accounts?.find(a => a.code === counterPartAccountCode) || {}
            form.setFieldValue('counterPartRowId', statementRowId)
          }
        }}
        onFinish={handleOk}
      >
        <Form.Item hidden name="id">
          <FormattedInputNumber />
        </Form.Item>
        <Form.Item hidden name="balanceSheetRowId" rules={[{ required: true, message: ' ' }]}>
          <Input />
        </Form.Item>
        <Form.Item hidden name="counterPartRowId" rules={[{ required: true, message: ' ' }]}>
          <Input />
        </Form.Item>
        <Form.Item hidden name="budgetingScenarioId">
          <FormattedInputNumber />
        </Form.Item>

        <Form.Item
          name="accountCode"
          rules={[{ required: true, message: ' ' }]}
          label={t('financialStatementsPage:source')}
        >
          <AccountSelect disabled />
        </Form.Item>

        <Form.Item name="value" rules={[{ required: true, message: ' ' }]} label={t('financialStatementsPage:value')}>
          <FormattedInputNumber style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="date" rules={[{ required: true, message: ' ' }]} label={t('financialStatementsPage:date')}>
          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="description"
          rules={[{ required: true, message: ' ' }]}
          label={t('financialStatementsPage:description')}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="counterPartAccountCode"
          rules={[{ required: true, message: ' ' }]}
          label={t('financialStatementsPage:counter-part')}
        >
          {counterPartSelectOptions(balanceSheetRowId)}
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default AmendmentModal
